'use client'

import { DateSelection } from '@repo/ui/DateSelection'
import { MatterHistoryFolderSelect } from '@repo/ui/MatterHistoryFolderSelect'
import { MatterHistoryTypeSelect } from '@repo/ui/MatterHistoryTypeSelect'
import { RetentionPolicySelect } from '@repo/ui/RetentionPolicySelect'
import { useDebounce } from '@uidotdev/usehooks'
import { useEffect, useRef, useState } from 'react'
import { useEmailCardStore } from '@repo/store/useEmailCardStore'
import { useMatterHistoryStore } from '@repo/store/useMatterHistoryStore'
import MUICheckbox from '@mui/material/Checkbox'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIStack from '@mui/material/Stack'
import MUITextField from '@mui/material/TextField'
import MUITypography from '@mui/material/Typography'

export const MatterHistorySettings = () => {
  const { emailSendingCurrentMatterData, matterHistorySettings, setMatterHistorySettings, emailType } =
    useEmailCardStore()
  const { fetchMatterHistoryPracticeDefaults } = useMatterHistoryStore()

  useEffect(() => {
    const fetchDefaultsOnNewEmail = async () => {
      await fetchMatterHistoryPracticeDefaults({ resolveParent: true })
    }

    emailType === 'new' && fetchDefaultsOnNewEmail()
  }, [emailType, fetchMatterHistoryPracticeDefaults])

  return (
    <MUIStack data-testid="MatterHistorySettings" direction={'column'} gap={4}>
      <MUITypography variant="body2" sx={{ mt: -1, mb: 5 }}>
          <em>These settings will apply to all emails in this conversation.</em>
      </MUITypography>

      <MUITextField
        label="Details - outbound messages"
        variant="outlined"
        type="text"
        value={matterHistorySettings?.matterHistoryDetailForSentEmail ?? ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMatterHistorySettings({ matterHistoryDetailForSentEmail: e.target.value })}
      />

       <MUITextField
        label="Details - inbound messages"
        variant="outlined"
        type="text"
        value={matterHistorySettings?.matterHistoryDetailForReceivedEmail ?? ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMatterHistorySettings({ matterHistoryDetailForReceivedEmail: e.target.value })}
      />

      <MatterHistoryFolderSelect
        value={matterHistorySettings?.matterHistoryFolderId ?? null}
        onChange={(matterHistoryFolderId: any) =>
          setMatterHistorySettings({ matterHistoryFolderId: matterHistoryFolderId })
        }
        workTypeId={emailSendingCurrentMatterData?.workTypeId}
      />
      <MatterHistoryTypeSelect
        value={matterHistorySettings?.matterHistoryTypeId ?? null}
        onChange={(id) => setMatterHistorySettings({ matterHistoryTypeId: id })}
      />
      <RetentionPolicySelect
        value={matterHistorySettings?.retentionPolicyId ?? null}
        onChange={(value: any) => setMatterHistorySettings({ retentionPolicyId: value })}
      />
      {matterHistorySettings?.retentionPolicyId && (
        <MUIRadioGroup
          name="RetentionDate"
          value={
            matterHistorySettings?.retentionDateType === 1
              ? 'Archive date'
              : matterHistorySettings?.retentionDateType === 2
                ? `Today's date`
                : null
          }
          onChange={(e) =>
            e.target.value === 'Archive date'
              ? setMatterHistorySettings({ retentionDateType: 1 })
              : e.target.value === `Today's date`
                ? setMatterHistorySettings({ retentionDateType: 2 })
                : null
          }
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            mt: -2,
            mb: 0
          }}
        >
          <MUITypography variant="body1" sx={{ alignSelf: 'center', mr: 4, mt: 4, textTransform: 'uppercase' }}>
            Base the retention date on:
          </MUITypography>
          <MUIFormControlLabel value={'Archive date'} control={<MUIRadio />} label="Archive date" sx={{ mr: 10 }} />
          <MUIFormControlLabel value={`Today's date`} control={<MUIRadio />} label="Today's date" />
        </MUIRadioGroup>
      )}
      <MUIStack direction="column" gap={1} mt={2} mb={6}>
        <MUITypography variant="body1">INCLUDE:</MUITypography>
        <MUIStack direction="row" gap={2}>
          <MUIRadioGroup
              name="IncludeTypeForSentEmail"
              value={
                matterHistorySettings?.isSaveEmailBody && matterHistorySettings?.isSaveEmailAttachments
                  ? 'BodyAndAttachments'
                  : matterHistorySettings?.isSaveEmailBody
                    ? 'BodyOnly'
                    : matterHistorySettings?.isSaveEmailAttachments
                      ? 'AttachmentsOnly'
                      : null
              }
              onChange={(e) =>
                e.target.value === 'BodyAndAttachments'
                  ? setMatterHistorySettings({ isSaveEmailBody: true, isSaveEmailAttachments: true })
                  : e.target.value === `BodyOnly`
                    ? setMatterHistorySettings({ isSaveEmailBody: true, isSaveEmailAttachments: false })
                    : e.target.value === `AttachmentsOnly`
                      ? setMatterHistorySettings({ isSaveEmailBody: false, isSaveEmailAttachments: true })
                    : null
              }
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                mt: -1,
                mb: 0
              }}
            >
              <MUIFormControlLabel value={'BodyAndAttachments'} control={<MUIRadio />} label="Message body and attachments" />
              <MUIFormControlLabel value={`BodyOnly`} control={<MUIRadio />} label="Message body only" />
              <MUIFormControlLabel value={`AttachmentsOnly`} control={<MUIRadio />} label="Attachments only" />
          </MUIRadioGroup>
        </MUIStack>

        <MUIStack direction="column" gap={2}>
          <MUIFormControlLabel
            sx={{ flex: 1 }}
            control={
              <MUICheckbox
                checked={matterHistorySettings?.isIgnoreInlineImages ?? false}
                onChange={(e: any) => setMatterHistorySettings({ isIgnoreInlineImages: e.target.checked })}
              />
            }
            label="Ignore inline images"
          />
          {matterHistorySettings?.isIgnoreInlineImages && (
            <MUIStack direction="row" gap={2} ml={1}>
              <MUITypography variant="body2" sx={{ alignSelf: 'center' }}>
                Smaller than:
              </MUITypography>
              <MUITextField
                variant="outlined"
                type="number"
                size="small"
                value={matterHistorySettings?.ignoreInlineImagesBelowKilobytes ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setMatterHistorySettings({ ignoreInlineImagesBelowKilobytes: e.target.value })
                }
              />
              <MUITypography variant="body2" sx={{ alignSelf: 'center' }}>
                KB
              </MUITypography>
            </MUIStack>
          )}

        </MUIStack>
      </MUIStack>
    </MUIStack>
  )
}
